import styled from '@emotion/styled'

import { Button } from '@/components/Button'
import { Image } from '@/components/Image'
import type { MediaProps } from '@/components/Media'
import { MediaContainer, type MediaContainerProps } from '@/components/MediaContainer'
import { clearBuilderProps } from '@/core/utils/dom/clearBuilderProps'
import { decodeURL } from '@/core/utils/dom/decodeURL'
import type { BlockButtonProps } from '@/models'
import { CONFIG } from '@/styles/emotion.config'

import { MediaListItem, type MediaListItemProps } from './MediaListItem'

export interface MediaListBlockProps extends MediaContainerProps, BlockButtonProps {
  midia: Omit<MediaProps, 'video'>
  items: MediaListItemProps[]
  events?: {
    key: string
    attrs: {
      attr: string
      value: string
    }[]
  }[]
}

const MediaListBlockStyles = styled.div({
  /* container */
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  /* container media */
  '& .ml--ma': {
    maxWidth: '28rem',
    flex: '1 0 100%',
    marginRight: 'auto',
  },

  /* container */
  '& .ml--ct': {
    margin: 'auto',
    maxWidth: '30rem',
  },

  /* container button */
  '& .ml--bn': {
    width: '100%',
    maxWidth: '80%',
    marginTop: '2rem',
  },

  [CONFIG.media.mobileLg]: {
    flexDirection: 'column',

    /* container */
    '& .ml--ct': {
      margin: '0',
      width: '100%',
      maxWidth: '28rem',
    },

    '& .ml--ma': {
      order: '0',
      margin: '0 auto 2rem',
      width: '100%',
    },

    /* button */
    '& .ml--bn': {
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
})

const MediaListBlock = ({
  midia,
  items,
  hasButton,
  button,
  buttonLabel,
  buttonModifier,
  buttonType,
  buttonValue,
  ...mediaContainerProps
}: MediaListBlockProps) => {
  const buttonProps = {
    buttonModifier: buttonModifier || button?.type,
    buttonLabel: buttonLabel || button?.label,
    buttonType: buttonType || button?.action?.type,
    buttonValue: buttonValue || button?.action?.value,
  }

  clearBuilderProps(mediaContainerProps)

  return (
    <MediaContainer {...mediaContainerProps}>
      <MediaListBlockStyles>
        <div className="ml--ma">
          {midia && (
            <Image data={midia.image} width="448" height="522" layout="responsive" alt="" />
          )}
        </div>
        <div className="ml--ct">
          <div className="ml--is">
            {items &&
              items.map((item, index) => <MediaListItem key={`ml_item_${index}`} {...item} />)}
          </div>

          {hasButton && buttonProps && (
            <div className="ml--bs">
              <Button
                action={buttonProps.buttonType}
                value={decodeURL(buttonProps.buttonValue)}
                modifier={buttonProps.buttonModifier || 'outline'}
                size="lg"
                className="ml--bn"
              >
                {buttonProps.buttonLabel}
              </Button>
            </div>
          )}
        </div>
      </MediaListBlockStyles>
    </MediaContainer>
  )
}

export default MediaListBlock
